import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, 
  Eye, 
  FileText, 
  Globe,
  CheckCircle,
  Users,
  BarChart3,
  ArrowRight
} from 'lucide-react';

const transparencyPoints = [
  {
    icon: FileText,
    title: 'Registro digital de participaciones',
    description: 'Cada participación queda registrada digitalmente en nuestra plataforma con fecha, hora y datos del participante.'
  },
  {
    icon: Eye,
    title: 'Cupo visible en tiempo real',
    description: 'El progreso del evento es visible para todos los participantes. Puedes ver cuántos cupos se han asignado en cualquier momento.'
  },
  {
    icon: Shield,
    title: 'Proceso conforme a condiciones publicadas',
    description: 'El evento se ejecuta siguiendo las condiciones establecidas y publicadas previamente. Sin cambios durante el proceso.'
  },
  {
    icon: Globe,
    title: 'Publicación de resultados',
    description: 'Los resultados de cada evento se publican dentro de la plataforma y se notifican a todos los participantes.'
  }
];

export default function TransparencyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <Shield className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Transparencia
          </h1>
          <p className="text-slate-300 text-lg">
            Nuestro compromiso con la claridad y la confianza
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="text-center mb-12">
          <p className="text-lg text-slate-600 leading-relaxed">
            En TuPrima creemos que la confianza se construye con transparencia. 
            Por eso, cada aspecto de nuestros eventos está diseñado para que 
            tengas visibilidad completa del proceso.
          </p>
        </div>

        {/* Transparency Points */}
        <div className="space-y-6 mb-12">
          {transparencyPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`transparency-point-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-800 mb-2">
                        {point.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Commitment Section */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-4 text-center">
            Nuestro compromiso
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium text-slate-700">Información clara</p>
            </div>
            <div className="text-center p-4">
              <Users className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium text-slate-700">Comunidad verificada</p>
            </div>
            <div className="text-center p-4">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium text-slate-700">Datos en tiempo real</p>
            </div>
          </div>
        </div>

        {/* Trust Stats - Dynamic from API */}
        <div className="mb-12" data-testid="trust-stats-section">
          <h2 className="text-xl font-bold text-slate-800 mb-6 text-center">
            Nuestra comunidad en números
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <Card className="text-center bg-slate-50">
              <CardContent className="p-6">
                <p className="text-3xl font-bold text-emerald-600" data-testid="stat-participants">+100</p>
                <p className="text-sm text-slate-600">Participantes</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-slate-50">
              <CardContent className="p-6">
                <p className="text-3xl font-bold text-blue-600" data-testid="stat-events">1</p>
                <p className="text-sm text-slate-600">Eventos</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-slate-50">
              <CardContent className="p-6">
                <p className="text-3xl font-bold text-purple-600" data-testid="stat-community">Activa</p>
                <p className="text-sm text-slate-600">Comunidad</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Estadísticas actualizadas periódicamente
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/">
            <Button 
              size="lg" 
              className="btn-primary rounded-full px-8"
              data-testid="view-active-event-btn"
            >
              Ver evento activo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Legal Note */}
        <div className="mt-12 p-6 bg-slate-100 rounded-xl">
          <p className="text-sm text-slate-600 text-center">
            Para más información sobre nuestras políticas y términos, visita nuestra sección de{' '}
            <Link to="/terms" className="text-emerald-600 hover:underline">
              Términos y Condiciones
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
